import React, { useState } from "react";

import { convertFullDateTimeToWithoutYearSecond } from "../../../libs/dateFormat";

import { MANAGER_ROLE } from "../../../consts/member";

import { UserState, useUserContext } from "../../../contexts/userContext";

import {
  ReviewItem,
  postReviewReply,
  updateReviewReply,
} from "../../../services/spaceServices";

import useSimplePopup from "../../../hooks/useSimplePopup";
import useFullScreenLoading from "../../../hooks/useFullScreenLoading";

import CommonTextArea from "../../TextArea";
import CommonButton from "../../Button";
import HorizontalRule from "../../Rule/Horizontal";
import StarScore from "../StarScore";
import CommonProfile from "../../MyInfo/Profile";

type Props = {
  review: ReviewItem;
  spaceId: string;
  onLoad?: () => void;
  onPopup?: (text: string, item: any) => void;
};

export default function TalkEachReview({
  review,
  spaceId,
  onLoad,
  onPopup,
}: Props) {
  const [user] = useUserContext();

  return (
    <div className="flex flex-col gap-5">
      <div className="flex gap-2.5">
        <CommonProfile src={review?.createdByThumbnail} />
        <div className="flex justify-between w-full">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-[5px]">
              <div>
                <StarScore score={review.rating ?? 0} />
              </div>
              <div className=" text-sm text-brand-neutral-85 font-semibold">
                {review.createdByNickName ?? ""}
              </div>
            </div>
            <div className="text-base">{review.content}</div>
          </div>
          <div className="text-xs text-[#999999]">
            {convertFullDateTimeToWithoutYearSecond(review.createdAt)}
          </div>
        </div>
      </div>
      {user.memberId === review.createdBy && (
        <div className="pl-[50px]">
          <CommonButton
            variant="tertiary"
            className="w-full py-[11px] text-sm text-brand-neutral-60"
            onClick={() => onPopup?.("modifyReview", review)}
          >
            리뷰 수정하기
          </CommonButton>
        </div>
      )}

      {review?.reply ? (
        review?.reply?.map((reply) => (
          <TalkEachReviewReply
            key={reply.lifeSpaceReviewId}
            review={review}
            reply={reply}
            user={user}
            spaceId={spaceId}
            onLoad={onLoad}
          />
        ))
      ) : (
        <TalkEachReviewReply
          review={review}
          user={user}
          spaceId={spaceId}
          onLoad={onLoad}
        />
      )}

      <HorizontalRule margin="0" />
    </div>
  );
}

export type TalkEachReviewReplyProps = {
  review: ReviewItem;
  reply?: ReviewItem;
  user: UserState;
  spaceId: string;
  onLoad?: () => void;
};

export const TalkEachReviewReply = ({
  review,
  reply,
  user,
  spaceId,
  onLoad,
}: TalkEachReviewReplyProps) => {
  const [content, setContent] = useState<string>(reply?.content ?? "");
  const [editMode, setEditMode] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [simplePopupComponent, { open }] = useSimplePopup({});

  return (
    <>
      {reply ? (
        <div className="pl-[50px]">
          <div className="p-[15px] rounded-[5px] flex flex-col gap-2.5 bg-[#f5f5f5]">
            <div className="flex gap-[5px] items-center">
              <CommonProfile
                src={reply?.createdByThumbnail}
                sizeVariant="reply"
              />
              <div className="flex-1 text-sm font-medium">
                {reply?.createdByNickName ?? ""}
              </div>
              <div className="text-xs text-[#999999]">
                {convertFullDateTimeToWithoutYearSecond(reply?.createdAt)}
              </div>
            </div>

            {editMode !== true && <div>{reply?.content}</div>}
            {reply.createdBy === user.memberId &&
              (editMode === true ? (
                <div className="relative">
                  <CommonTextArea
                    placeholder={
                      "이용자의 평가에 대한 댓글을 남겨주세요\n(10자 이상)"
                    }
                    value={content}
                    onChange={(e) => {
                      setContent(e.target.value);
                    }}
                  />
                  <div className="absolute bottom-2.5 right-2.5 flex gap-[5px]">
                    <CommonButton
                      variant="tertiary"
                      className="px-4 py-2"
                      onClick={async () => {
                        setEditMode(false);
                      }}
                    >
                      취소
                    </CommonButton>
                    <CommonButton
                      variant="tertiary"
                      className="px-4 py-2"
                      onClick={async () => {
                        setIsLoading(true);
                        try {
                          await updateReviewReply(
                            reply?.lifeSpaceReviewId ?? "",
                            {
                              content,
                            }
                          );
                          await onLoad?.();
                          setEditMode(false);
                        } catch (e: any) {
                          if (e?.response?.data?.error) {
                            open(
                              e?.response?.data?.error?.replace(". ", ".\n")
                            );
                          } else {
                            open("리뷰 댓글을 수정하는데 실패했습니다.");
                          }
                        } finally {
                          setIsLoading(false);
                        }
                      }}
                    >
                      수정
                    </CommonButton>
                  </div>
                </div>
              ) : (
                <CommonButton
                  variant="tertiary"
                  className="py-2 text-brand-neutral-60 font-medium"
                  onClick={() => setEditMode(true)}
                >
                  수정
                </CommonButton>
              ))}
          </div>
        </div>
      ) : user.roles?.includes(MANAGER_ROLE.CAFETERIA) ? (
        <div className="pl-[50px]">
          <div className="p-[15px] rounded-[5px] flex flex-col gap-2.5 bg-[#f5f5f5]">
            <div className="flex gap-[5px] items-center">
              <CommonProfile
                src={user?.profileImage?.filePath}
                sizeVariant="reply"
              />
              <div className="flex-1 text-sm font-medium">{user.nickname}</div>
            </div>
            <div className="relative">
              <CommonTextArea
                placeholder={
                  "이용자의 평가에 대한 댓글을 남겨주세요\n(10자 이상)"
                }
                value={content}
                onChange={(e) => {
                  setContent(e.target.value);
                }}
              />
              <CommonButton
                variant="tertiary"
                className="absolute bottom-2.5 right-2.5 px-4 py-2"
                onClick={async () => {
                  if (content.length < 10) {
                    open("댓글은 10자 이상 입력해주세요.");
                    return;
                  }

                  setIsLoading(true);
                  try {
                    await postReviewReply(review.lifeSpaceReviewId, {
                      spaceId,
                      content,
                    });
                    await onLoad?.();
                  } catch (e: any) {
                    if (e?.response?.data?.error) {
                      open(e?.response?.data?.error?.replace(". ", ".\n"));
                    } else {
                      open("리뷰 댓글을 등록하는데 실패했습니다.");
                    }
                  } finally {
                    setIsLoading(false);
                  }
                }}
              >
                등록
              </CommonButton>
            </div>
          </div>
        </div>
      ) : null}
      {loadingComponent}
      {simplePopupComponent}
    </>
  );
};
